import { getStatsOverview } from '../admin-api';
import type { StatsOverview } from '../admin-api';
import { widgetById, renderWidget } from '../dashboard-widgets';
import type { WidgetId } from '../dashboard-widgets';
import { loadLayout, saveLayout } from '../dashboard-layout';
import type { DashboardLayout } from '../dashboard-layout';

export async function mountDashboard(container: HTMLElement): Promise<() => void> {
  let alive = true;
  let data: StatsOverview | null = null;
  let editMode = false;
  let layout: DashboardLayout = loadLayout();
  let draggedId: WidgetId | null = null;

  try {
    data = await getStatsOverview();
  } catch {
    container.innerHTML = '<p class="text-red-400 p-4">Failed to load dashboard.</p>';
    return () => {};
  }
  if (!alive) return () => {};

  function visibleWidgets(): WidgetId[] {
    return layout.order.filter(id => !layout.hidden.includes(id));
  }

  function render(): void {
    if (!data) return;
    const visible = visibleWidgets();
    const hiddenWidgets = layout.hidden
      .map(id => widgetById(id))
      .filter(Boolean);

    container.innerHTML = `
      <div class="flex items-center justify-between mb-4">
        <h2 class="font-heading text-gold text-lg">Dashboard</h2>
        <button id="adm-dash-edit-toggle" class="btn btn-stone min-h-11 px-4 text-sm">
          ${editMode ? 'Done' : 'Edit Layout'}
        </button>
      </div>

      <div id="adm-dash-grid" class="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 ${editMode ? 'admin-edit-mode' : ''}">
        ${visible.map(id => {
          const w = widgetById(id);
          return w ? renderWidget(w, data!, editMode) : '';
        }).join('')}
      </div>

      ${editMode && hiddenWidgets.length > 0 ? `
        <div class="frame-inset p-4">
          <h3 class="text-muted text-[13px] mb-2">Hidden Widgets</h3>
          <div class="flex flex-wrap gap-2">
            ${hiddenWidgets.map(w => `
              <button class="btn btn-stone min-h-11 px-3 text-xs" data-restore-widget="${w!.id}">
                + ${w!.title}
              </button>
            `).join('')}
          </div>
        </div>
      ` : ''}
    `;
    wire();
  }

  function wire(): void {
    container.querySelector('#adm-dash-edit-toggle')?.addEventListener('click', () => {
      editMode = !editMode;
      render();
    });

    if (editMode) {
      container.querySelectorAll<HTMLButtonElement>('[data-hide-widget]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.hideWidget as WidgetId;
          layout.hidden.push(id);
          saveLayout(layout);
          render();
        });
      });

      container.querySelectorAll<HTMLButtonElement>('[data-restore-widget]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.restoreWidget as WidgetId;
          layout.hidden = layout.hidden.filter(h => h !== id);
          saveLayout(layout);
          render();
        });
      });

      const grid = container.querySelector<HTMLElement>('#adm-dash-grid');
      if (grid) {
        grid.querySelectorAll<HTMLElement>('.admin-widget').forEach(el => {
          el.addEventListener('dragstart', (e) => {
            draggedId = el.dataset.widgetId as WidgetId;
            el.classList.add('opacity-40');
            e.dataTransfer?.setData('text/plain', draggedId);
          });

          el.addEventListener('dragend', () => {
            el.classList.remove('opacity-40');
            draggedId = null;
          });

          el.addEventListener('dragover', (e) => {
            e.preventDefault();
            el.classList.add('ring-2', 'ring-gold/40');
          });

          el.addEventListener('dragleave', () => {
            el.classList.remove('ring-2', 'ring-gold/40');
          });

          el.addEventListener('drop', (e) => {
            e.preventDefault();
            el.classList.remove('ring-2', 'ring-gold/40');
            const targetId = el.dataset.widgetId as WidgetId;
            if (!draggedId || draggedId === targetId) return;
            const fromIdx = layout.order.indexOf(draggedId);
            const toIdx = layout.order.indexOf(targetId);
            if (fromIdx === -1 || toIdx === -1) return;
            layout.order.splice(fromIdx, 1);
            layout.order.splice(toIdx, 0, draggedId);
            saveLayout(layout);
            render();
          });
        });
      }
    }

    container.querySelectorAll<HTMLButtonElement>('[data-admin-nav]').forEach(btn => {
      btn.addEventListener('click', () => {
        container.dispatchEvent(new CustomEvent('admin:navigate', {
          detail: { section: btn.dataset.adminNav },
          bubbles: true,
        }));
      });
    });
  }

  render();
  return () => { alive = false; };
}
